import { useRef } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import { Link as RouterLink } from 'react-router-dom'
import { productImagesFor } from '@/content/products'
import { home, standards } from '@/content/copy'
import { colors, fonts, layout } from '@/theme'
import { Container } from '@/components/ui/Container'
import { Eyebrow } from '@/components/ui/Eyebrow'

// Curated hero rotation: strongest spark/lifestyle shots (see IMAGE_USAGE_PLAN.md).
const heroSlides = [
  productImagesFor('tf120')[5], // gold glove + live sparks (most on-brand)
  productImagesFor('tf130')[0], // dramatic bench sparks
  productImagesFor('tf129')[4], // pipe welding
  productImagesFor('tf111')[4], // MIG sparks
].filter(Boolean)

// Deterministic ember field (no Math.random — keeps prerender stable).
const EMBERS = [
  { l: 6, s: 3, d: 0.0, t: 9, x: 18 },
  { l: 12, s: 2, d: 2.4, t: 11, x: -12 },
  { l: 19, s: 4, d: 4.1, t: 10, x: 24 },
  { l: 26, s: 2, d: 1.2, t: 12, x: -18 },
  { l: 33, s: 3, d: 5.5, t: 9.5, x: 10 },
  { l: 42, s: 2, d: 3.0, t: 13, x: -8 },
  { l: 9, s: 2, d: 6.2, t: 10.5, x: 16 },
  { l: 22, s: 3, d: 7.4, t: 11.5, x: -20 },
  { l: 15, s: 2, d: 8.1, t: 9, x: 12 },
  { l: 30, s: 3, d: 2.0, t: 12.5, x: -14 },
]

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`
const glow = keyframes`
  0%, 100% { opacity: 0.14; }
  50%      { opacity: 0.26; }
`
const drift = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(44px, 44px, 0); }
`
const bob = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.8; }
  50%      { transform: translateY(7px); opacity: 0.25; }
`
const emberRise = keyframes`
  0%   { transform: translate(0, 0) scale(1); opacity: 0; }
  8%   { opacity: 0.85; }
  85%  { opacity: 0.5; }
  100% { transform: translate(var(--ex, 16px), -78vh) scale(0.5); opacity: 0; }
`
const sheen = keyframes`
  0%       { background-position: 220% 0; }
  55%,100% { background-position: -60% 0; }
`
// Right-to-left slider: each slide pushes in from the right and out to the left.
// Old exit (25→28%) is in lockstep with the next slide's entry (0→3%) so the two
// tile the viewport exactly — no background gap during the transition.
const HERO_SLIDE_SECS = 5.5 // per slide; total cycle = slides * this
const slideMove = keyframes`
  0%   { opacity: 0; transform: translateX(100%); }
  3%   { opacity: 1; transform: translateX(0); }
  25%  { opacity: 1; transform: translateX(0); }
  28%  { opacity: 1; transform: translateX(-100%); }
  29%  { opacity: 0; transform: translateX(-100%); }
  100% { opacity: 0; transform: translateX(100%); }
`

/** Staggered CSS entrance — pure CSS so it plays in the prerendered (no-JS) HTML too. */
const rise = (delay: number) => ({
  animation: `${riseIn} 0.7s cubic-bezier(0.22, 1, 0.36, 1) both`,
  animationDelay: `${delay}s`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
})

/** Full-bleed cinematic hero: industrial texture, drifting embers, cursor-tracked gold light and a molten heading. */
export function Hero() {
  const heroRef = useRef<HTMLDivElement>(null)

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = heroRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    el.style.setProperty('--mx', `${e.clientX - r.left}px`)
    el.style.setProperty('--my', `${e.clientY - r.top}px`)
  }

  return (
    <Box
      ref={heroRef}
      onMouseMove={onMove}
      sx={{ position: 'relative', minHeight: { xs: '88dvh', md: '94dvh' }, display: 'flex', alignItems: 'center', overflow: 'hidden' }}
    >
      {heroSlides.map((img, i) => (
        <Box
          key={img.src}
          component="img"
          src={img.src}
          srcSet={img.srcSm ? `${img.srcSm} 600w, ${img.src} 1200w` : undefined}
          sizes="100vw"
          alt={i === 0 ? 'True Forge welding gloves in use' : ''}
          width={img.width}
          height={img.height}
          fetchPriority={i === 0 ? 'high' : undefined}
          loading={i === 0 ? undefined : 'lazy'}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0,
            willChange: 'transform, opacity',
            // Negative phase offset so slide 0 loads already centered (no blank flash / LCP hit).
            animation: `${slideMove} ${HERO_SLIDE_SECS * heroSlides.length}s cubic-bezier(0.65, 0, 0.35, 1) infinite`,
            animationDelay: `${i * HERO_SLIDE_SECS - HERO_SLIDE_SECS * 0.5}s`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: i === 0 ? 1 : 0, transform: 'none' },
          }}
        />
      ))}

      {/* Legibility gradient (left→right) + bottom vignette to seat the strip */}
      <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, ${colors.bg} 0%, rgba(13,13,13,0.76) 46%, rgba(13,13,13,0.28) 100%)` }} />
      <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(0deg, ${colors.bg} 0%, transparent 32%)` }} />

      {/* Subtle gold dot-grid tech texture, slowly drifting (transform-based → GPU, no per-frame repaint) */}
      <Box
        sx={{
          position: 'absolute',
          inset: '-48px',
          backgroundImage: 'radial-gradient(rgba(212,175,55,0.18) 1px, transparent 1.4px)',
          backgroundSize: '44px 44px',
          opacity: 0.35,
          willChange: 'transform',
          animation: `${drift} 26s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />

      {/* Warm gold glow, gently breathing */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(55% 52% at 16% 82%, rgba(212,175,55,0.9) 0%, transparent 70%)',
          opacity: 0.18,
          animation: `${glow} 7s ease-in-out infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />

      {/* Cursor-tracked molten light — a composited blob moved by transform (no full-screen repaint) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 680,
          height: 680,
          borderRadius: '50%',
          pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(212,175,55,0.16), transparent 70%)',
          transform: 'translate3d(var(--mx, 20vw), var(--my, 72vh), 0) translate(-50%, -50%)',
          willChange: 'transform',
        }}
      />

      {/* Drifting embers */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', '@media (prefers-reduced-motion: reduce)': { display: 'none' } }}>
        {EMBERS.map((e, i) => (
          <Box
            key={i}
            style={{ ['--ex' as string]: `${e.x}px` }}
            sx={{
              position: 'absolute',
              bottom: -12,
              left: `${e.l}%`,
              width: e.s,
              height: e.s,
              borderRadius: '50%',
              backgroundColor: colors.gold,
              boxShadow: `0 0 6px ${colors.gold}`,
              opacity: 0,
              animation: `${emberRise} ${e.t}s linear ${e.d}s infinite`,
            }}
          />
        ))}
      </Box>

      <Container sx={{ position: 'relative', pt: `${layout.headerHeight}px` }}>
        <Box sx={{ maxWidth: 760 }}>
          <Box sx={rise(0)}>
            <Eyebrow dot>{home.heroEyebrow}</Eyebrow>
          </Box>
          <Box sx={rise(0.08)}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                background: `linear-gradient(100deg, ${colors.white} 0%, ${colors.white} 34%, #FFF3D0 47%, #C9B678 58%, ${colors.white} 74%, ${colors.white} 100%)`,
                backgroundSize: '250% 100%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                animation: `${sheen} 6s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none', backgroundPosition: '0 0' },
              }}
            >
              {home.heroTitle}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ ...rise(0.18), color: colors.white, opacity: 0.86, mt: 3, maxWidth: 560 }}>
            {home.heroSubtitle}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ ...rise(0.28), mt: 5 }}>
            <Button variant="contained" component={RouterLink} to="/contact">
              Request a Quote
            </Button>
            <Button variant="outlined" component={RouterLink} to="/products">
              Explore Products
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Bottom bar: certification signals + scroll cue */}
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, pb: { xs: 3, md: 4 } }}>
        <Container>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ ...rise(0.42), borderTop: `1px solid ${colors.border}`, pt: { xs: 2.5, md: 3 } }}
          >
            <Stack direction="row" spacing={{ xs: 1.75, md: 3 }} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontFamily: fonts.label, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'text.secondary' }}>
                Built to Standard
              </Typography>
              {standards.items.map((s) => (
                <Typography key={s.code} sx={{ fontFamily: fonts.label, fontSize: { xs: 11, md: 13 }, letterSpacing: '0.08em', color: colors.white, opacity: 0.9 }}>
                  {s.code}
                </Typography>
              ))}
            </Stack>

            <Box sx={{ display: { xs: 'none', md: 'block' } }} aria-hidden>
              <Box sx={{ width: 22, height: 36, borderRadius: 999, border: `1px solid ${colors.borderStrong}`, position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 7,
                    ml: '-2px',
                    width: 4,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: colors.gold,
                    animation: `${bob} 1.8s ease-in-out infinite`,
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  )
}
